import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Search, Megaphone, FileText, Bot } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getAllIndustryPages, allClusters } from "@/data/contentClusters";

const pillarIcons: Record<string, React.ElementType> = {
  "geo": Search,
  "ai-ads": Megaphone,
  "ai-content": FileText,
  "ai-agents": Bot,
};

const pillarColors: Record<string, string> = {
  "geo": "from-primary/20 to-primary/5 border-primary/30",
  "ai-ads": "from-orange-500/20 to-orange-500/5 border-orange-500/30",
  "ai-content": "from-green-500/20 to-green-500/5 border-green-500/30",
  "ai-agents": "from-purple-500/20 to-purple-500/5 border-purple-500/30",
};

export default function Industries() {
  const allIndustries = getAllIndustryPages();

  // Group industries by primary pillar
  const industriesByPillar = allClusters.map(cluster => ({
    pillar: cluster.pillar,
    industries: allIndustries.filter(ind => ind.pillarSlugs[0] === cluster.pillar.slug),
  }));

  return (
    <Layout>
      <SEO 
        title="Industries We Serve | B2B, SaaS, Enterprise"
        description="AI-powered marketing solutions for SaaS, B2B, enterprise software, cloud infrastructure, and AI startups. Industry-specific strategies for growth."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs 
            items={[
              { label: "Home", href: "/" },
              { label: "Industries" }
            ]}
            className="mb-8"
          />
          <AnimatedSection className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-4">
              <Building2 className="w-3 h-3 mr-1" />
              Industry Expertise
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Industries <span className="text-gradient">We Serve</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              AI-powered marketing solutions tailored for your industry. From SaaS startups 
              to enterprise software, we understand your unique challenges.
            </p>
            <Button 
              size="lg" 
              className="rounded-full"
              data-cal-link="thecloudseo/30-min-meeting"
              data-cal-namespace="30-min-meeting"
              data-cal-config='{"layout":"month_view"}'
            >
              Discuss Your Industry
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Industries by Pillar */}
      {industriesByPillar.map((group, groupIndex) => {
        if (group.industries.length === 0) return null;
        
        const Icon = pillarIcons[group.pillar.slug] || Building2;
        const colorClass = pillarColors[group.pillar.slug] || "";

        return (
          <section key={group.pillar.slug} className="py-16 border-t border-border">
            <div className="container-wide">
              <AnimatedSection>
                <div className="flex items-center gap-4 mb-8">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClass} border`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{group.pillar.title} Industries</h2>
                    <p className="text-muted-foreground">{group.pillar.subtitle}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.industries.map((industry, index) => (
                    <motion.div
                      key={industry.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link 
                        to={`/industries/${industry.slug}`}
                        className="group block p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                      >
                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {industry.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          {industry.description}
                        </p>
                        
                        {/* Stats Preview */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          {industry.stats.slice(0, 2).map((stat, i) => (
                            <div key={i} className="text-center p-2 rounded-lg bg-secondary/50">
                              <div className="text-lg font-bold text-primary">{stat.value}</div>
                              <div className="text-xs text-muted-foreground">{stat.label}</div>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center text-sm font-medium text-primary">
                          Learn More
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Don't See Your Industry?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              We work with B2B and technology companies across many verticals. 
              Let's discuss how AI-powered marketing can work for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/30-min-meeting"
                data-cal-namespace="30-min-meeting"
                data-cal-config='{"layout":"month_view"}'
              >
                Book a Consultation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full"
                asChild
              >
                <Link to="/services">
                  View All Services
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
